import type { KeyboardEvent } from 'react';
import clsx from 'clsx';
import { Input } from '@oysterjs/ui/input';

import { normalizeHex6 } from '~/components/library/lib/color';

/**
 * Compact hex-color input — a native color picker swatch paired with a
 * monospaced text field. Used throughout the HTML style editor for primary /
 * secondary / heading / table colours. The `value` is what's shown in the
 * swatch (with placeholder fallback for empty input) while `textValue` is
 * the literal string in the text box, so callers can keep displaying the
 * placeholder colour without the field appearing pre-populated.
 */
export function ColorInput({
  value,
  textValue,
  onChange,
  placeholder,
  inputClassName,
  label,
  spellCheck,
  onTextBlur,
  onTextKeyDown
}: {
  value: string;
  textValue: string;
  onChange: (hex: string | undefined) => void;
  placeholder?: string;
  inputClassName?: string;
  /** Names the swatch, and the text field as "<label> hex". */
  label?: string;
  spellCheck?: boolean;
  onTextBlur?: () => void;
  onTextKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
}) {
  const pickerValue = normalizeHex6(value) ?? normalizeHex6(placeholder ?? '') ?? '#000000';

  return (
    <div className="flex items-center gap-1.5">
      <input
        type="color"
        aria-label={label}
        value={pickerValue}
        onChange={(e) => onChange(e.target.value)}
        className="h-7 w-9 rounded border border-neutral-300 dark:border-neutral-600 cursor-pointer p-0.5"
      />
      <Input
        type="text"
        aria-label={label ? `${label} hex` : undefined}
        value={textValue}
        spellCheck={spellCheck}
        onBlur={onTextBlur}
        onKeyDown={onTextKeyDown}
        onChange={(e) => {
          const raw = e.target.value;
          const normalized = normalizeHex6(raw);
          onChange((normalized ?? raw) || undefined);
        }}
        placeholder={placeholder}
        className={clsx('font-mono text-xs', inputClassName ?? 'w-24')}
      />
    </div>
  );
}
